/**
 * 3D Hero Scroll Animation (No-ImportMap Version for Firefox Stability)
 */

(() => {
    // --- 1. CONFIGURATION ---
    const animationConfig = {
        modelUrl: 'https://cdn.shopify.com/3d/models/4ed2d16bccc88968/magnetika13_compressed_1_.glb',
        hdrUrl: 'https://cdn.shopify.com/s/files/1/0572/5955/7000/files/photo_studio_01_1k.hdr?v=1762016823',
        modelScale: 0.015,
        modelPosition: { x: 0, y: 0, z: 0 },
        cameraPosition: { x: 0, y: -1, z: 4 },
        cameraFov: 50,
        ambientLight: { color: 0xffffff, intensity: 0.2 },
        directionalLight: { color: 0xffffff, intensity: 0.8, position: { x: 5, y: 5, z: 5 } },
        annotations: [] 
    };

    const initializedSections = new Set();
    let threeGlobals = null;

    // --- 2. DEPENDENCY LOADING (ESM.SH STRATEGY) ---
    async function loadDependencies() {
        if (threeGlobals) return threeGlobals;

        let THREE, gsap, ScrollTrigger, GLTFLoader, RGBELoader;

        try {
            // 1. Пытаемся взять GSAP из глобальной области (обычно в Shopify он уже есть)
            gsap = window.gsap;
            ScrollTrigger = window.ScrollTrigger;

            if (!gsap || !ScrollTrigger) {
                console.warn('GSAP not found globally, attempting dynamic load...');
                console.error('GSAP is required but not loaded.');
                return null;
            }

            // 2. Загружаем THREE.js через ESM.SH (без importmap!)
            // Это решает проблему Firefox, так как пути уже прописаны внутри файла
            const threeUrl = "https://esm.sh/three@0.164.1";
            const gltfUrl = "https://esm.sh/three@0.164.1/examples/jsm/loaders/GLTFLoader.js";
            const rgbeUrl = "https://esm.sh/three@0.164.1/examples/jsm/loaders/RGBELoader.js";

            // Импортируем само ядро
            if (window.THREE) {
                THREE = window.THREE;
            } else {
                const threeModule = await import(threeUrl);
                THREE = threeModule;
                window.THREE = THREE; // Делаем доступным глобально
            }

            const gltfModule = await import(gltfUrl);
            const rgbeModule = await import(rgbeUrl);

            GLTFLoader = gltfModule.GLTFLoader;
            RGBELoader = rgbeModule.RGBELoader;

        } catch (error) {
            console.error('Critical Loading Error (Firefox/Safari):', error);
            return null;
        }

        gsap.registerPlugin(ScrollTrigger);
        threeGlobals = { THREE, gsap, ScrollTrigger, GLTFLoader, RGBELoader };
        return threeGlobals;
    }

    // if (window.ScrollTrigger) {
    //     ScrollTrigger.config({ ignoreMobileResize: true });
    //     ScrollTrigger.normalizeScroll({
    //         allowNestedScroll: true,
    //         lockAxis: false,
    //         momentum: (self) => Math.min(3, self.velocityY / 1000),
    //         type: "touch,wheel"
    //     });
    // }

    if (window.ScrollTrigger) {
        ScrollTrigger.normalizeScroll(true); 
    }

    // --- 3. MAIN INITIALIZATION ---
    async function initThreeAnimation(sectionElement) {
        const sectionId = sectionElement.dataset.sectionId;
        if (initializedSections.has(sectionId)) return;

        // SCROLL LOCK
        const preventDefault = (e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
        };

        const toggleScrollLock = (shouldLock) => {
            const html = document.documentElement;
            const body = document.body;
            
            if (shouldLock) {
                const lockStyles = { 
                    overflow: 'hidden', 
                    touchAction: 'none', 
                    overscrollBehavior: 'none',
                    webkitOverflowScrolling: 'touch'
                };
                Object.assign(html.style, lockStyles);
                Object.assign(body.style, lockStyles);
                window.addEventListener('touchmove', preventDefault, { passive: false, capture: true });
                window.addEventListener('wheel', preventDefault, { passive: false, capture: true });
            } else {
                ['overflow', 'touchAction', 'overscrollBehavior', 'webkitOverflowScrolling'].forEach(p => {
                    html.style[p] = '';
                    body.style[p] = '';
                });
                window.removeEventListener('touchmove', preventDefault, { passive: false, capture: true });
                window.removeEventListener('wheel', preventDefault, { passive: false, capture: true });
            }
        };

        toggleScrollLock(true); 

        const deps = await loadDependencies();
        if (!deps) {
            toggleScrollLock(false);
            console.error("Dependencies failed to load, unlocking scroll.");
            return;
        }
        const { THREE, gsap, ScrollTrigger, GLTFLoader, RGBELoader } = deps;

        const canvas = sectionElement.querySelector(`#webgl-canvas-${sectionId}`);
        const scrollContainer = sectionElement.querySelector(`#scroll-container-${sectionId}`);
        
        if (!canvas || !scrollContainer) return;

        canvas.style.webkitTouchCallout = 'none';
        canvas.style.webkitUserSelect = 'none';
        canvas.style.userSelect = 'none';
        canvas.style.outline = 'none';

        // --- LOADER ---
        const LoaderController = {
            el: sectionElement.querySelector(`#loader-${sectionId}`),
            textEl: sectionElement.querySelector(`#loader-text-${sectionId}`),
            icon: sectionElement.querySelector('.loader-logo-icon'),
            logoText: sectionElement.querySelector('.loader-logo-text'),
            loadingText: sectionElement.querySelector('#loader-text-1'),
            
            updateProgress(progress) {
                if(this.textEl) this.textEl.textContent = `${progress}%`;
            },

            onAssetsLoaded() {
                if (this.loadingText) {
                    gsap.to(this.loadingText, { opacity: 0, duration: 0.3 });
                }
                this.playIntro();
            },

            playIntro() {
                const tl = gsap.timeline({
                    onComplete: () => {
                        this.playExit();
                    }
                });

                gsap.set(this.icon, { opacity: 0, scale: 0.9 });
                
                gsap.set(this.logoText, { 
                    opacity: 1, 
                    clipPath: 'inset(0 100% 0 0)',
                    WebkitClipPath: 'inset(0 100% 0 0)' 
                });

                tl.to(this.icon, { opacity: 1, scale: 1, duration: 0.6, ease: 'power2.out' })
                  .to(this.logoText, { 
                      clipPath: 'inset(0 0% 0 0)', 
                      WebkitClipPath: 'inset(0 0% 0 0)',
                      duration: 0.8, 
                      ease: 'power2.out' 
                  }, '-=0.2')
                  .to({}, { duration: 0.5 });
            },

            playExit() {
                window.scrollTo(0, 0);
                if (window.history.scrollRestoration) window.history.scrollRestoration = 'manual';

                gsap.to(this.el, { 
                    xPercent: 100,
                    duration: 1.5,
                    ease: "power2.inOut",
                    onComplete: () => {
                        this.el.style.display = 'none';
                        toggleScrollLock(false); 
                        startMainSceneLogic();
                    }
                });
            }
        };

        // --- HEADER LOGIC ---
        const header = document.getElementById('CustomHeader');
        const headerWrapper = document.querySelector('.header-wrapper');
        const iconsBlock = document.querySelector('.custom-header__icons');
        let initialHeaderClasses = headerWrapper ? headerWrapper.className : '';

        const setHeaderTheme = (active, isMobile = false) => {
            if (!header || !headerWrapper) return;
            const elementsToReset = [
                header, '.custom-header__nav a', '.custom-header__icon', 
                '.burger-menu .line', '.logo--light', '.logo--dark', '.logo-text--dark'
            ];
            if(iconsBlock) elementsToReset.push(iconsBlock);

            if (active) {
                if (window.headerResetTimer) clearTimeout(window.headerResetTimer);
                
                if (iconsBlock && isMobile) {
                    gsap.to(iconsBlock, { autoAlpha: 0, duration: 0.4, overwrite: 'auto' });
                }

                if (!headerWrapper.classList.contains('header-style-transparent')) {
                    headerWrapper.classList.add('header-style-transparent');
                }
                headerWrapper.classList.remove('header-style-transparent-dark');

                gsap.to(header, { backgroundColor: 'transparent', borderBottomColor: 'transparent', duration: 0.4, overwrite: 'auto' });
                gsap.to('.custom-header__nav a', { color: 'white', duration: 0.4, overwrite: 'auto' });
                gsap.to('.custom-header__icon', { color: 'white', duration: 0.4, overwrite: 'auto' });
                gsap.to('.burger-menu .line', { backgroundColor: 'white', duration: 0.4, overwrite: 'auto' });
                gsap.to('.logo--light', { opacity: 1, autoAlpha: 1, duration: 0.4, overwrite: 'auto' });
                gsap.to('.logo--dark, .logo-text--dark', { opacity: 0, autoAlpha: 0, duration: 0.4, overwrite: 'auto' });

            } else {
                window.headerResetTimer = setTimeout(() => {
                    gsap.killTweensOf(elementsToReset);
                    gsap.set(elementsToReset, { clearProps: "all" });
                    headerWrapper.className = initialHeaderClasses;
                }, 10);
            }
        };

        // --- THREE.JS SETUP ---
        const config = animationConfig;
        const breakPoint = 768;
        const isMobile = window.innerWidth < breakPoint;
        const currentScale = isMobile ? 0.010 : config.modelScale;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(config.cameraFov, window.innerWidth / window.innerHeight, 0.1, 1000);
        camera.position.set(config.cameraPosition.x, config.cameraPosition.y, config.cameraPosition.z);

        // [COMPATIBILITY FIX]
        const renderer = new THREE.WebGLRenderer({ 
            canvas: canvas, 
            antialias: true, 
            alpha: true,
            premultipliedAlpha: false
        });
        
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMappingExposure = 2.0;
        renderer.setClearColor(0x000000, 0);

        const ambientLight = new THREE.AmbientLight(config.ambientLight.color, config.ambientLight.intensity);
        scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(config.directionalLight.color, config.directionalLight.intensity);
        directionalLight.position.set(config.directionalLight.position.x, config.directionalLight.position.y, config.directionalLight.position.z);
        scene.add(directionalLight);

        // --- LOADING ASSETS ---
        const loadingManager = new THREE.LoadingManager();
        const loader = new GLTFLoader(loadingManager);
        let model;

        loadingManager.onProgress = (url, itemsLoaded, itemsTotal) => {
            const progress = Math.round((itemsLoaded / itemsTotal) * 100);
            LoaderController.updateProgress(progress);
        };

        loadingManager.onLoad = () => {
            if (model) renderer.compile(scene, camera);
            setTimeout(() => {
                LoaderController.onAssetsLoaded();
            }, 100);
        };

        if (config.hdrUrl) {
            new RGBELoader(loadingManager).load(config.hdrUrl, function (texture) {
                texture.mapping = THREE.EquirectangularReflectionMapping;
                scene.environment = texture;
            });
        }

        loader.load(config.modelUrl, (gltf) => {
            model = gltf.scene;
            model.scale.set(currentScale, currentScale, currentScale);
            model.position.set(config.modelPosition.x, config.modelPosition.y, config.modelPosition.z);
            scene.add(model);
        });

        // --- MAIN SCENE LOGIC ---
        function startMainSceneLogic() {
            if (!model) return;

            const group1 = model.getObjectByName('body_1');
            const group2 = model.getObjectByName('body_2');
            const group3 = model.getObjectByName('valve_1');
            const group4 = model.getObjectByName('filter_1');
            const group5 = model.getObjectByName('lid_1');

            const startPos1 = group1 ? group1.position.clone() : new THREE.Vector3();
            const startPos2 = group2 ? group2.position.clone() : new THREE.Vector3();
            const startPos3 = group3 ? group3.position.clone() : new THREE.Vector3();
            const startPos4 = group4 ? group4.position.clone() : new THREE.Vector3();

            // Hover & Interaction
            const raycaster = new THREE.Raycaster();
            const mouse = new THREE.Vector2();
            let intersectedObject = null;
            let isHoverActive = false; 
            const hoverTargets = [group1, group2, group3, group4, group5].filter(obj => obj);

            const interactionMap = [
                { part: group1, popupId: 'popup-2' },
                { part: group2, popupId: 'popup-5' },
                { part: group3, popupId: 'popup-4' },
                { part: group4, popupId: 'popup-3' },
                { part: group5, popupId: 'popup-1' }
            ];
            
            const closeAllPopups = () => {
                document.querySelectorAll('.hotspot-info').forEach(el => el.classList.remove('is-visible'));
            };

            window.addEventListener('click', (event) => {
                if (!isHoverActive) return;
                raycaster.setFromCamera(mouse, camera);
                const intersects = raycaster.intersectObjects(hoverTargets, false);
                if (intersects.length > 0) {
                    const hitObject = intersects[0].object;
                    const match = interactionMap.find(item => item.part === hitObject);
                    if (match) {
                        const popup = document.getElementById(match.popupId);
                        if (popup) {
                            if (popup.classList.contains('is-visible')) {
                                popup.classList.remove('is-visible');
                            } else {
                                closeAllPopups();
                                popup.classList.add('is-visible');
                            }
                        }
                    }
                } else {
                    closeAllPopups();
                }
            });

            window.addEventListener('mousemove', (event) => {
                mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
                mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
            });
            window.addEventListener('touchstart', (event) => {
                if (event.touches.length > 0) {
                    mouse.x = (event.touches[0].clientX / window.innerWidth) * 2 - 1;
                    mouse.y = -(event.touches[0].clientY / window.innerHeight) * 2 + 1;
                }
            });

            const viewportElement = sectionElement.querySelector('.scroll-animation-viewport');
            const root = document.documentElement;
            
            const anno1 = sectionElement.querySelector('#anno-1');
            const anno2 = sectionElement.querySelector('#anno-2');
            const anno3 = sectionElement.querySelector('#anno-3');
            const anno1_bg = sectionElement.querySelector('#anno-1-bg');
            const anno2_bg = sectionElement.querySelector('#anno-2-bg');
            const anno3_bg = sectionElement.querySelector('#anno-3-bg');
            const anno1_t1 = sectionElement.querySelector('#anno-1-text_1');
            const anno1_t2 = sectionElement.querySelector('#anno-1-text_2');
            const anno2_t1 = sectionElement.querySelector('#anno-2-text-1');
            const anno2_t2 = sectionElement.querySelector('#anno-2-text-2');
            const anno2_t3 = sectionElement.querySelector('#anno-2-text-3');
            const anno3_cols = sectionElement.querySelectorAll('.anno-3-column');
            const touchHint = sectionElement.querySelector('#touch-me-hint');

            const cameraLookAtTarget = new THREE.Vector3(0, 0, 0);
            const randomX = () => gsap.utils.random(10, 90) + '%';
            const randomY = () => gsap.utils.random(10, 90) + '%';
            const initialCameraPos = config.cameraPosition;
            const initialLookAt = { x: 0, y: 0.5, z: 0 };

            let mm = gsap.matchMedia();

            mm.add({
                isDesktop: "(min-width: 769px) and (min-height: 600px)", 
                isMobile: "(max-width: 768px), (max-height: 600px)"
            }, (context) => {
                let { isDesktop, isMobile } = context.conditions;

                const totalScrollHeight = isDesktop ? (config.annotations.length * 150 + 100) : 1400;
                scrollContainer.style.height = `${totalScrollHeight}vh`;
                
                ScrollTrigger.refresh();

                const tl = gsap.timeline({
                    scrollTrigger: {
                        trigger: viewportElement,
                        pin: true,
                        start: 'top top',
                        end: isDesktop ? '+=500%' : '+=1200%',
                        scrub: isDesktop ? 1.5 : 0.5,
                        anticipatePin: 1,
                        onEnter: () => setHeaderTheme(true, isMobile),
                        onLeave: () => setHeaderTheme(false, isMobile),
                        onEnterBack: () => setHeaderTheme(true, isMobile),
                        onLeaveBack: () => setHeaderTheme(false, isMobile)
                    }
                });
                
                const flashOrder = [group5, group1, group4, group2, group3]; 
                let hintTimeline = null;

                const playTouchHintAnimation = () => {
                    const isGoingDown = tl && tl.scrollTrigger && tl.scrollTrigger.direction === 1;

                    if (!isGoingDown) {
                        if (hintTimeline) {
                            hintTimeline.kill(); 
                            hintTimeline = null;
                        }
                        
                        gsap.set(touchHint, { opacity: 0 });
                        isHoverActive = false;

                        flashOrder.forEach(part => {
                            if (part?.material?.emissive) {
                                gsap.set(part.material.emissive, { r: 0, g: 0, b: 0 });
                            }
                        });
                        
                        if (intersectedObject) {
                            gsap.set(intersectedObject.scale, { x: 1, y: 1, z: 1 });
                            intersectedObject = null;
                        }
                        return;
                    }

                    if (hintTimeline && hintTimeline.isActive()) return;
                    
                    hintTimeline = gsap.timeline({
                        onStart: () => {
                            isHoverActive = false; 
                            flashOrder.forEach(part => {
                                if (part?.material?.emissive) {
                                    gsap.set(part.material.emissive, { r: 0, g: 0, b: 0 });
                                }
                            });
                            if (intersectedObject) {
                                gsap.to(intersectedObject.scale, { x: 1, y: 1, z: 1, duration: 0.2 });
                                intersectedObject = null;
                            }
                        },
                        onComplete: () => {
                            isHoverActive = true; 
                            gsap.to(touchHint, { opacity: 0, duration: 0.5, delay: 2 });
                        }
                    });

                    flashOrder.forEach((part) => {
                        if (part && part.material) {
                            hintTimeline.to(part.material.emissive, {
                                r: 0.4, g: 0.4, b: 0.4,
                                duration: 0.2,
                                yoyo: true,
                                repeat: 1,
                                ease: "power1.inOut"
                            });
                        }
                    });

                    hintTimeline.to(touchHint, { opacity: 1, duration: 0.5, ease: "power2.out" }, "<");
                };
                                                                            
                isHoverActive = false;
                cameraLookAtTarget.set(initialLookAt.x, initialLookAt.y, initialLookAt.z);

                 if (isDesktop) {
                    gsap.set([anno1, anno2, anno3], { x: "-100vw", opacity: 1, pointerEvents: "auto" });
                    gsap.set([anno1_bg, anno2_bg, anno3_bg], { opacity: 0 });
                    
                    gsap.set(model.scale, { x: config.modelScale, y: config.modelScale, z: config.modelScale });
                    gsap.set(model.position, { x: config.modelPosition.x, y: config.modelPosition.y, z: config.modelPosition.z });
                    gsap.set(camera.position, { x: config.cameraPosition.x, y: config.cameraPosition.y, z: config.cameraPosition.z });

                    tl.to(camera.position, { z: 3.5, y: 3, ease: "sine.inOut", duration: 1.5 })
                      .to(model.rotation, { y: Math.PI * 0.5, ease: "sine.inOut", duration: 1.5 }, "<")
                      .to(root, { '--grad1-x': randomX(), '--grad1-y': randomY(), ease: "power1.inOut", duration: 1.5 }, "<")
                      .to(anno1, { x: "0vw", duration: 1.5, ease: "sine.inOut" }, "<")
                      .to(anno1_bg, { opacity: 1, duration: 0.8, ease: "sine.out" }, "<");
                      
                     tl.to(model.rotation, { 
                        y: Math.PI * -0.10, z: Math.PI * -0.25, x: Math.PI * -0.12, 
                        ease: "sine.inOut", duration: 1.5,
                        onUpdate: function() {
                            isHoverActive = this.progress() >= 0.70;
                        },
                        onComplete: () => { isHoverActive = true; },
                        onReverseComplete: () => { isHoverActive = false; }
                    }) 
                      .to(model.position, { y: -0.25, ease: "sine.inOut", duration: 1.5 }, "<")
                      .to(group1?.position || {}, { y: startPos1.y + 50, ease: "sine.inOut", duration: 1.5 }, "<")
                      .to(group2?.position || {}, { y: startPos2.y - 50, ease: "sine.inOut", duration: 1.5 }, "<")
                      .to(group3?.position || {}, { y: startPos3.y - 50, z: startPos3.z + 25, ease: "sine.inOut", duration: 1.5 }, "<")
                      .to(group4?.position || {}, { y: startPos4.y + 20, ease: "sine.inOut", duration: 1.5 }, "<")
                      .to(root, { '--grad1-x': randomX(), '--grad1-y': randomY(), ease: "power1.inOut", duration: 1.5 }, "<")
                      .to(anno1, { x: "100vw", duration: 1.5, ease: "sine.inOut" }, "<")
                      .to(anno2, { x: "0vw", duration: 1.5, ease: "sine.inOut" }, "<")
                      .to(anno1_bg, { opacity: 0, duration: 0.6, ease: "sine.in" }, "<")
                      .to(anno2_bg, { opacity: 1, duration: 0.8, ease: "sine.out" }, "<");

                    tl.to(model.rotation, { 
                        y: -Math.PI * 0.25, z: 0, x: 0, ease: "sine.inOut", duration: 1.5,
                        onStart: () => { isHoverActive = false; },
                        onReverseComplete: () => { isHoverActive = true; }
                    }, "+=1.5")
                      .to(model.position, { y: 0, ease: "sine.inOut", duration: 1.5 }, "<")
                      .to(group1?.position || {}, { y: startPos1.y, ease: "sine.inOut", duration: 1.5 }, "<")
                      .to(group2?.position || {}, { y: startPos2.y, ease: "sine.inOut", duration: 1.5 }, "<")
                      .to(group3?.position || {}, { y: startPos3.y, z: startPos3.z, ease: "sine.inOut", duration: 1.5 }, "<")
                      .to(group4?.position || {}, { y: startPos4.y, ease: "sine.inOut", duration: 1.5 }, "<")
                      .to(cameraLookAtTarget, { x: -2.5, y: -1.5, ease: "sine.inOut", duration: 1.5 }, "<")
                      .to(root, { '--grad1-x': randomX(), '--grad1-y': randomY(), ease: "power1.inOut", duration: 1.5 }, "<")
                      .to(anno2, { x: "100vw", duration: 1.5, ease: "sine.inOut" }, "<")
                      .to(anno3, { x: "0vw", duration: 1.5, ease: "sine.inOut" }, "<")
                      .to(anno2_bg, { opacity: 0, duration: 0.6, ease: "sine.in" }, "<")
                      .to(anno3_bg, { opacity: 1, duration: 0.8, ease: "sine.out" }, "<");

                    tl.to(model.rotation, { y: Math.PI * 0, ease: "sine.inOut", duration: 1.5 }, "+=0.75")
                      .to(cameraLookAtTarget, { x: initialLookAt.x, ease: "sine.inOut", duration: 1.5 }, "<")
                      .to(model.position, { y: -2.0, ease: "sine.inOut", duration: 1.5 }, "<")
                      .to(camera.position, { x: initialCameraPos.x, z: initialCameraPos.z, y: -3.0, ease: "sine.inOut", duration: 1.5 }, "<")
                      .to(anno3, { x: "100vw", duration: 1.5, ease: "sine.inOut" }, "<")
                      .to(anno3_bg, { opacity: 0, duration: 0.6, ease: "sine.in" }, "<", "+=1.5");
                }
                
                if (isMobile) {
                    const isLandscape = window.innerWidth > window.innerHeight;
                    const mobileScale = 0.015; 
                    const mobileY = isLandscape ? -0.5 : 0; 
                    const step2_RotY = Math.PI * -0.10;
                    const step2_RotZ = Math.PI * 0.065;
                    const step2_RotX = Math.PI * 0.025;
                    const fastText = 1.5;
                    const fadeSpeed = 1.5;
                    const slowModel = 3;

                    gsap.set([anno1, anno2, anno3], { x: 0, opacity: 0, pointerEvents: "none" });
                    gsap.set([anno1_t1, anno1_t2, anno2_t1, anno2_t2, anno2_t3, anno3_cols], { opacity: 0, y: 20 });
                    gsap.set([anno1_bg, anno2_bg, anno3_bg], { opacity: 0 });
                    
                    gsap.set(model.scale, { x: mobileScale, y: mobileScale, z: mobileScale });
                    gsap.set(model.position, { x: 0, y: mobileY, z: 0 }); 
                    gsap.set(camera.position, { x: config.cameraPosition.x, y: config.cameraPosition.y, z: config.cameraPosition.z });

                    tl.to(camera.position, { z: 3.5, y: 3, ease: "sine.inOut", duration: slowModel })
                      .to(model.position, { y: mobileY, ease: "sine.inOut", duration: slowModel }, "<") 
                      .to(model.rotation, { y: Math.PI * 0.5, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(anno1_bg, { opacity: 1, duration: slowModel, ease: "sine.out" }, "<")
                      .to(anno1, { opacity: 1, duration: 1 }, "<+=" + (slowModel - 1));

                    tl.to(anno1_t1, { opacity: 1, y: 0, duration: fastText, ease: "power1.out" }, ">") 
                      .to(anno1_t1, { opacity: 0, y: -20, duration: fadeSpeed, ease: "power1.in" }, ">")
                      .to(anno1_t2, { opacity: 1, y: 0, duration: fastText, ease: "power1.out" }, ">")
                      .to(anno1_t2, { opacity: 0, y: -20, duration: fadeSpeed, ease: "power1.in" }, ">");

                    tl.to(model.rotation, { 
                        y: step2_RotY,
                        z: step2_RotZ,
                        x: step2_RotX,
                        ease: "sine.inOut", duration: slowModel,
                    }, ">")
                      .to(model.scale, { x: mobileScale - 0.0015, z: mobileScale - 0.0015, y: mobileScale - 0.0015, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(model.position, { y: -0.25, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(group1?.position || {}, { y: startPos1.y + 50, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(group2?.position || {}, { y: startPos2.y - 50, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(group3?.position || {}, { z: startPos3.z + 50, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(group4?.position || {}, { y: startPos3.y + 20, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(anno1_bg, { opacity: 0, duration: 1 }, "<")
                      .to(anno1, { opacity: 0, duration: 0.5 }, "<") 
                      .to(anno2_bg, { opacity: 1, duration: 2 }, "<")
                      .to(anno2, { opacity: 1, duration: 0.5 }, "<+=" + (slowModel - 1));

                    tl.to(anno2_t1, { opacity: 1, y: 0, duration: fastText }, ">")
                      .to(anno2_t1, { opacity: 0, y: -20, duration: fadeSpeed }, ">")
                      .to(anno2_t2, { opacity: 1, y: 0, duration: fastText }, ">")
                      .to(anno2_t2, { opacity: 0, y: -20, duration: fadeSpeed }, ">")
                      .to(anno2_t3, { opacity: 1, y: 0, duration: fastText }, ">")
                      .to(anno2_t3, { opacity: 0, y: -20, duration: fadeSpeed }, ">")
                      .call(playTouchHintAnimation, null, ">") 
                      .to({}, { duration: 3.5 })
                      .to(touchHint, { opacity: 0, duration: 0.5 }, ">");

                    tl.to(model.rotation, { 
                        y: Math.PI * 0.25, x: 0.35, z: 0, 
                        ease: "sine.inOut", duration: slowModel,
                        onStart: () => { 
                            isHoverActive = false;
                            if (hintTimeline) {
                                hintTimeline.kill();
                                hintTimeline = null;
                            }
                            gsap.set(touchHint, { opacity: 0 });
                            flashOrder.forEach(part => {
                                if (part?.material?.emissive) {
                                    gsap.set(part.material.emissive, { r: 0, g: 0, b: 0 });
                                }
                            });
                            document.querySelectorAll('.hotspot-info').forEach(el => el.classList.remove('is-visible'));
                            if (intersectedObject) {
                                gsap.set(intersectedObject.scale, { x: 1, y: 1, z: 1 });
                                intersectedObject = null;
                            }
                         },
                        onReverseComplete: () => { isHoverActive = true; }
                    }, ">")
                      .to(model.scale, { x: mobileScale + 0.0013, z: mobileScale + 0.0013, y: mobileScale + 0.0013, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(model.position, { x: -0.08, y: 0.5, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(group1?.position || {}, { y: startPos1.y, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(group2?.position || {}, { y: startPos2.y, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(group3?.position || {}, { z: startPos3.z, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(group4?.position || {}, { y: startPos4.y, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(cameraLookAtTarget, { x: 0, z: -1.5, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(anno2_bg, { opacity: 0, duration: 1 }, "<")
                      .to(anno2, { opacity: 0, duration: 0.5 }, "<") 
                      .to(anno3_bg, { opacity: 1, duration: 2 }, "<")
                      .to(anno3, { opacity: 1, duration: 0.5 }, "<+=" + (slowModel - 1));

                    anno3_cols.forEach((col, i) => {
                        tl.to(col, { opacity: 1, y: 0, duration: fastText }, ">");
                        if (i < anno3_cols.length - 1) {
                            tl.to(col, { opacity: 0, y: -20, duration: fadeSpeed }, ">");
                        } else {
                            tl.to(col, { opacity: 0, y: -20, duration: fadeSpeed }, ">+=0.5"); 
                        }
                    });

                    tl.to(model.rotation, { x: 0.2, y: Math.PI * 0, ease: "sine.inOut", duration: slowModel }, ">")
                      .to(cameraLookAtTarget, { x: initialLookAt.x, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(model.position, { y: -1.0, x: 0, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(camera.position, { x: initialCameraPos.x, z: initialCameraPos.z, y: -3.0, duration: slowModel }, "<")
                      .to(model.scale, { x: mobileScale - 0.0008, z: mobileScale - 0.0008, y: mobileScale - 0.0008, ease: "sine.inOut", duration: slowModel }, "<")
                      .to(anno3_bg, { opacity: 0, duration: 1 }, "<")
                      .to(anno3, { opacity: 0, duration: 0.5 }, "<");
                }
            });

            // Resize Handler
            let lastWidth = window.innerWidth;
            
            const onResize = () => {
                if (isMobile && window.innerWidth === lastWidth) return;
                lastWidth = window.innerWidth;

                camera.aspect = window.innerWidth / window.innerHeight;
                camera.updateProjectionMatrix();
                renderer.setSize(window.innerWidth, window.innerHeight);
                renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
            };
            window.addEventListener('resize', onResize);

            // Cleanup
            sectionElement.addEventListener('shopify:section:unload', () => {
                window.removeEventListener('resize', onResize);
                mm.revert(); 
            });

            // Render Loop
            let targetMouseX = 0;
            let targetMouseY = 0;
            const parallaxStrength = 0.3;

            const animate = () => {
                requestAnimationFrame(animate);

                if (!isHoverActive) {
                    if (intersectedObject) {
                        if (intersectedObject.material?.emissive) gsap.to(intersectedObject.material.emissive, { r: 0, g: 0, b: 0, duration: 0.3 });
                        gsap.to(intersectedObject.scale, { x: 1, y: 1, z: 1, duration: 0.3, ease: "sine.inOut" });
                        intersectedObject = null;
                    }
                } else {
                    raycaster.setFromCamera(mouse, camera);
                    const intersects = raycaster.intersectObjects(hoverTargets, false);
                    if (intersects.length > 0) {
                        const object = intersects[0].object;
                        if (intersectedObject !== object) {
                            if (intersectedObject) {
                                if (intersectedObject.material?.emissive) gsap.to(intersectedObject.material.emissive, { r: 0, g: 0, b: 0, duration: 0.3 });
                                gsap.to(intersectedObject.scale, { x: 1, y: 1, z: 1, duration: 0.3, ease: "sine.inOut" });
                            }
                            intersectedObject = object;
                            if (intersectedObject.material?.emissive) gsap.to(intersectedObject.material.emissive, { r: 0.2, g: 0.2, b: 0.2, duration: 0.3 });
                            gsap.to(intersectedObject.scale, { x: 1.1, y: 1.1, z: 1.1, duration: 0.3, ease: "sine.inOut" });
                        }
                    } else {
                        if (intersectedObject) {
                            if (intersectedObject.material?.emissive) gsap.to(intersectedObject.material.emissive, { r: 0, g: 0, b: 0, duration: 0.3 });
                            gsap.to(intersectedObject.scale, { x: 1, y: 1, z: 1, duration: 0.3, ease: "sine.inOut" });
                            intersectedObject = null;
                        }
                    }
                }

                targetMouseX += (mouse.x - targetMouseX) * 0.05;
                targetMouseY += (mouse.y - targetMouseY) * 0.05;
                const offsetX = targetMouseX * parallaxStrength;
                const offsetY = targetMouseY * parallaxStrength;

                camera.position.x += offsetX;
                camera.position.y += offsetY;
                camera.lookAt(cameraLookAtTarget);
                renderer.render(scene, camera);
                camera.position.x -= offsetX;
                camera.position.y -= offsetY;
            };
            animate();
        }

        initializedSections.add(sectionId);
    }

    document.addEventListener('DOMContentLoaded', () => {
        document.querySelectorAll('.three-js-scroll-section').forEach(section => initThreeAnimation(section));
    });
    if (window.shopify && window.shopify.designMode) {
        document.addEventListener('shopify:section:load', (event) => {
            if (event.target.classList.contains('three-js-scroll-section')) initThreeAnimation(event.target);
        });
    }
})();